import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DistributionClient } from '../lib/api';
import { formatDuration } from '../lib/format';
import type { CybertubeCatalogItem, CybertubeProgress, CybertubeWatch as WatchPayload } from '../lib/types';
import { useI18n } from '../lib/use-i18n';

const SAVE_INTERVAL_MS = 10_000;
const OFFLINE_POLL_MS = 6_000;

export function CyberTubeWatch({ client, sourceId, resume, onOpen, onClose, onProgress }: {
  client: DistributionClient;
  sourceId: string;
  resume?: CybertubeProgress;
  onOpen: (sourceId: string) => void;
  onClose: () => void;
  onProgress: (sourceId: string, currentTime: number, duration: number) => void;
}) {
  const { locale, t } = useI18n();
  const video = useRef<HTMLVideoElement | null>(null);
  const shell = useRef<HTMLDivElement | null>(null);
  const [watch, setWatch] = useState<WatchPayload | null>(null);
  const [failed, setFailed] = useState(false);
  /**
   * Where the current media element starts inside the video. Muxed streams are
   * produced from `?start=`, so the element's own currentTime is relative.
   */
  const [baseOffset, setBaseOffset] = useState(0);
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimer = useRef(0);
  const lastSaved = useRef(0);

  const duration = watch?.playback.durationSec ?? watch?.durationSec ?? 0;
  const seekable = watch?.playback.seekable ?? false;

  // --------------------------------------------------------------- load video

  useEffect(() => {
    let cancelled = false;
    setWatch(null);
    setFailed(false);
    setPosition(0);
    setBaseOffset(0);
    client.cybertubeWatch(sourceId)
      .then((payload) => {
        if (cancelled) return;
        setWatch(payload);
        const startAt = resume && resume.sourceId === sourceId && resume.currentTime > 5
          ? Math.floor(resume.currentTime)
          : 0;
        setBaseOffset(payload.playback.seekable ? 0 : startAt);
        setPosition(startAt);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
    // `resume` is only an initial seed; re-reading it must not reload the video.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, sourceId]);

  const src = useMemo(() => {
    if (!watch) return '';
    return client.cybertubeStreamUrl(watch.playback, watch.playback.seekable ? 0 : baseOffset);
  }, [baseOffset, client, watch]);

  // Seekable sources start from a real byte offset, so jump once metadata is in.
  const onLoadedMetadata = useCallback(() => {
    const element = video.current;
    if (!element || !watch?.playback.seekable) return;
    if (position > 0 && Math.abs(element.currentTime - position) > 2) {
      element.currentTime = position;
    }
  }, [position, watch]);

  // ------------------------------------------------------- offline hand-off

  useEffect(() => {
    if (!watch || watch.playback.mode !== 'stream') return;
    if (!watch.playback.offline || watch.playback.offline.state === 'failed') return;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const next = await client.cybertubeWatch(sourceId);
        if (cancelled || next.playback.mode !== 'local') return;
        // The offline copy landed: switch to it and keep the viewer's position.
        // That file is range-served, so seeking starts working from here on.
        const at = position;
        setWatch(next);
        setBaseOffset(0);
        setPosition(at);
      } catch {
        // A failed poll just means we try again on the next tick.
      }
    }, OFFLINE_POLL_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [client, position, sourceId, watch]);

  // ------------------------------------------------------------- progress

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!playing || !duration || position <= 0) return;
      if (Math.abs(position - lastSaved.current) < 5) return;
      lastSaved.current = position;
      onProgress(sourceId, position, duration);
    }, SAVE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [duration, onProgress, playing, position, sourceId]);

  useEffect(() => () => {
    if (position > 5 && duration > 0) onProgress(sourceId, position, duration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------- controls

  const showControls = useCallback(() => {
    setControlsVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setControlsVisible(false), 3200);
  }, []);

  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  const togglePlay = useCallback(() => {
    const element = video.current;
    if (!element) return;
    if (element.paused) void element.play().catch(() => undefined);
    else element.pause();
  }, []);

  /**
   * Seeking works two ways. A range-served file moves the element's own clock;
   * an on-the-fly mux cannot answer byte offsets, so the stream is re-requested
   * from the new second and the element restarts at zero against a new base.
   */
  const seekTo = useCallback((seconds: number) => {
    const target = Math.max(0, Math.min(duration || seconds, seconds));
    const element = video.current;
    if (seekable && element) {
      element.currentTime = target;
      setPosition(target);
      return;
    }
    setBaseOffset(Math.floor(target));
    setPosition(target);
  }, [duration, seekable]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onClose(); return; }
      if (event.key === ' ' || event.key === 'k') { event.preventDefault(); togglePlay(); showControls(); return; }
      if (event.key === 'ArrowRight') { seekTo(position + 10); showControls(); return; }
      if (event.key === 'ArrowLeft') { seekTo(position - 10); showControls(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, position, seekTo, showControls, togglePlay]);

  function toggleFullscreen() {
    const node = shell.current;
    if (!node) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void node.requestFullscreen?.().catch(() => undefined);
  }

  const progressPercent = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;
  const offline = watch?.playback.offline;

  return (
    <div className="cybertube-watch" role="dialog" aria-modal="true" aria-label={watch?.title || t('cybertube')}>
      <div className="cybertube-watch-main">
        <div
          className={`cybertube-stage ${controlsVisible ? '' : 'idle'}`}
          ref={shell}
          onMouseMove={showControls}
          onClick={showControls}
        >
          {failed ? (
            <div className="empty-state">
              <b>{t('temporarilyUnavailable')}</b>
              <span>{t('tryAgainSoon')}</span>
            </div>
          ) : !watch ? (
            <div className="cybertube-stage-loading"><div className="loading-line" /></div>
          ) : (
            <video
              key={src}
              ref={video}
              className="cybertube-video"
              src={src}
              autoPlay
              playsInline
              muted={muted}
              onLoadedMetadata={onLoadedMetadata}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onTimeUpdate={(event) => setPosition(baseOffset + event.currentTarget.currentTime)}
              onEnded={() => { setPlaying(false); onProgress(sourceId, duration, duration); }}
              onError={() => setFailed(true)}
            />
          )}

          <button className="cybertube-watch-close" data-focusable="true" onClick={onClose} aria-label={t('close')}>×</button>

          {watch && (
            <div className={`cybertube-controls ${controlsVisible ? 'visible' : ''}`}>
              <input
                className="cybertube-seek"
                type="range"
                min={0}
                max={Math.max(1, Math.round(duration))}
                value={Math.round(position)}
                data-focusable="true"
                aria-label={t('cybertubeSeek')}
                onChange={(event) => seekTo(Number(event.target.value))}
              />
              <div className="cybertube-controls-row">
                <button data-focusable="true" onClick={togglePlay} aria-label={playing ? t('pause') : t('play')}>
                  {playing ? '❚❚' : '▶'}
                </button>
                <button data-focusable="true" onClick={() => setMuted((value) => !value)} aria-label={t('mute')}>
                  {muted ? '🔇' : '🔊'}
                </button>
                <span className="cybertube-time">
                  {formatDuration(Math.round(position), locale)} / {formatDuration(Math.round(duration), locale)}
                </span>
                <span className="cybertube-progress-track"><i style={{ width: `${progressPercent}%` }} /></span>
                <button data-focusable="true" onClick={toggleFullscreen} aria-label={t('fullscreen')}>⛶</button>
              </div>
            </div>
          )}
        </div>

        {watch && (
          <div className="cybertube-meta">
            <h1>{watch.title}</h1>
            <div className="cybertube-meta-row">
              {watch.channelAvatar && <img src={watch.channelAvatar} alt="" />}
              <div>
                <b>{watch.channelTitle}</b>
                <small>{[watch.viewCountText, watch.publishedText].filter(Boolean).join(' · ')}</small>
              </div>
              {offline && offline.state !== 'failed' && watch.playback.mode === 'stream' && (
                <span className="cybertube-offline-chip" title={t('cybertubeOfflineHint')}>
                  {t('cybertubeOfflinePreparing')} {Math.round(offline.progress)}%
                </span>
              )}
            </div>
            {watch.description && <p className="cybertube-description">{watch.description}</p>}
          </div>
        )}
      </div>

      {watch && watch.related.length > 0 && (
        <aside className="cybertube-related">
          <h2>{t('cybertubeRelated')}</h2>
          {watch.related.map((item) => (
            <RelatedCard key={item.sourceId} item={item} onOpen={onOpen} />
          ))}
        </aside>
      )}
    </div>
  );
}

function RelatedCard({ item, onOpen }: {
  item: CybertubeCatalogItem;
  onOpen: (sourceId: string) => void;
}) {
  const { locale } = useI18n();
  return (
    <button className="cybertube-related-card" data-focusable="true" onClick={() => onOpen(item.sourceId)}>
      <span
        className="cybertube-related-art"
        style={item.thumbnail ? { backgroundImage: `url("${item.thumbnail}")` } : undefined}
      >
        {item.durationSec ? <small>{formatDuration(item.durationSec, locale)}</small> : null}
      </span>
      <span className="cybertube-related-copy">
        <b>{item.title}</b>
        <small>{item.channelTitle}</small>
      </span>
    </button>
  );
}
